import { GridColumn, utils } from "@ucl/library";
import { IBoltTask } from "@bolt/core";
import { DueInGridColumn } from "./DueInGridColumn";

/**
 * Configuration interface for BoltTask grid columns
 * Allows users to override specific column properties while maintaining defaults
 */
export interface BoltTaskGridColumnConfiguration {
  taskNumber_AsString?: Partial<GridColumn>;
  dueIn?: Partial<GridColumn>;
  taskTemplateName?: Partial<GridColumn>;
  stepName?: Partial<GridColumn>;
  taskStatus_AsString?: Partial<GridColumn>;
  dueDate?: Partial<GridColumn>;
  assigneeName?: Partial<GridColumn>;
  createdBy_AsName?: Partial<GridColumn>;
  createdAt?: Partial<GridColumn>;
  workflow?: Partial<GridColumn>;
  isRush?: Partial<GridColumn>;
  sla?: Partial<GridColumn>;
  completedBy?: Partial<GridColumn>;
  completedDate?: Partial<GridColumn>;
  updatedBy_AsName?: Partial<GridColumn>;
  updatedAt?: Partial<GridColumn>;
  startedBy_AsName?: Partial<GridColumn>;
  startedAt?: Partial<GridColumn>;
  id?: Partial<GridColumn>;
}

function deriveSLAStatus(
  _field: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: { [key: string]: any }
): string {
  if (data["dueDate"] === undefined || data["dueDate"] === null) {
    return "";
  }

  const isCompleted =
    data["taskStatus_AsString"].toLowerCase() == "Completed".toLowerCase();
  const currentSecondsPastEpoch = Date.now();
  const dueDateAsSecondsPastEpoch = new Date(data["dueDate"]).valueOf();
  const completedDateAsSecondsPastEpoch = new Date(
    data["completedDate"]
  ).valueOf();

  if (!isCompleted && currentSecondsPastEpoch <= dueDateAsSecondsPastEpoch) {
    return "In SLA";
  }

  if (!isCompleted && currentSecondsPastEpoch > dueDateAsSecondsPastEpoch) {
    return "Out of SLA";
  }

  if (
    isCompleted &&
    completedDateAsSecondsPastEpoch <= dueDateAsSecondsPastEpoch
  ) {
    return "In SLA";
  }

  if (
    isCompleted &&
    completedDateAsSecondsPastEpoch > dueDateAsSecondsPastEpoch
  ) {
    return "Out of SLA";
  }

  return "";
}

/**
 * Creates the default column configuration for BoltTask grid
 * @returns Record of field names to GridColumn instances
 */
export const defaultBoltTaskColumns: Record<string, GridColumn> = {
  taskNumber_AsString: new GridColumn({
    field: "taskNumber_AsString",
    headerText: "Task ID",
    type: "number",
    maxWidth: 400,
  }),
  dueIn: new DueInGridColumn({
    field: "dueIn",
    headerText: "Due In",
    type: "string",
    allowSearching: false,
    allowFiltering: false,
    allowSorting: false,
    maxWidth: 400,
  }),
  taskTemplateName: new GridColumn({
    field: "taskTemplateName",
    headerText: "Task Template",
    type: "string",
    minWidth: 300,
    maxWidth: 400,
  }),
  stepName: new GridColumn({
    field: "stepName",
    headerText: "Workflow Step",
    type: "string",
    maxWidth: 400,
  }),
  taskStatus_AsString: new GridColumn({
    field: "taskStatus_AsString",
    headerText: "Status",
    type: "string",
    maxWidth: 400,
  }),
  dueDate: new GridColumn({
    field: "dueDate",
    headerText: "Due Date",
    format: { format: "MM/dd/yyyy hh:mm:ss a", type: "date" },
    type: "datetime",
    allowSearching: false,
    allowFiltering: false,
    template: (row: IBoltTask) => {
      return utils.date.formatUtcDateTime((row.dueDate as any) || "", true);
    },
    maxWidth: 400,
  }),
  assigneeName: new GridColumn({
    field: "assigneeName",
    headerText: "Assignee",
    type: "string",
    maxWidth: 400,
  }),
  createdBy_AsName: new GridColumn({
    field: "createdBy_AsName",
    headerText: "Created By",
    type: "string",
    maxWidth: 400,
  }),
  createdAt: new GridColumn({
    field: "createdAt",
    headerText: "Created Date",
    format: { format: "MM/dd/yyyy hh:mm:ss a", type: "date" },
    type: "datetime",
    allowSearching: false,
    allowFiltering: false,
    maxWidth: 400,
    template: (row: IBoltTask) => {
      return utils.date.formatUtcDateTime(row.createdAt || "", true);
    },
  }),
  workflow: new GridColumn({
    field: "workflow",
    headerText: "Multi Step Workflow",
    type: "string",
    maxWidth: 400,
  }),
  isRush: new GridColumn({
    field: "isRush",
    headerText: "Rush",
    type: "boolean",
    displayAsCheckBox: true,
    textAlign: "Center",
    allowSearching: false,
    maxWidth: 400,
  }),
  sla: new GridColumn({
    field: "sla",
    headerText: "SLA Status",
    type: "string",
    maxWidth: 400,
    valueAccessor: (field, data) => deriveSLAStatus(field, data),
  }),
  completedBy: new GridColumn({
    field: "completedBy",
    headerText: "Completed By",
    type: "string",
    maxWidth: 400,
  }),
  completedDate: new GridColumn({
    field: "completedDate",
    headerText: "Completed Date",
    format: { format: "MM/dd/yyyy hh:mm:ss a", type: "date" },
    type: "datetime",
    allowSearching: false,
    allowFiltering: false,
    maxWidth: 400,
  }),
  updatedBy_AsName: new GridColumn({
    field: "updatedBy_AsName",
    headerText: "Updated By",
    type: "string",
    maxWidth: 400,
  }),
  updatedAt: new GridColumn({
    field: "updatedAt",
    headerText: "Updated Date",
    format: { format: "MM/dd/yyyy hh:mm:ss a", type: "date" },
    type: "datetime",
    allowSearching: false,
    allowFiltering: false,
    maxWidth: 400,
    template: (row: IBoltTask) => {
      return utils.date.formatUtcDateTime(row.updatedAt || "", true);
    },
  }),
  startedBy_AsName: new GridColumn({
    field: "startedBy_AsName",
    headerText: "Started By",
    type: "string",
    maxWidth: 400,
  }),
  startedAt: new GridColumn({
    field: "startedAt",
    headerText: "Started Date",
    format: { format: "MM/dd/yyyy hh:mm:ss a", type: "date" },
    type: "datetime",
    allowSearching: false,
    allowFiltering: false,
    maxWidth: 400,
    template: (row: IBoltTask) => {
      return utils.date.formatUtcDateTime(row.startedAt || "", true);
    },
  }),
  id: new GridColumn({
    field: "id",
    headerText: "Identifier",
    type: "string",
    visible: false,
  }),
};

export const defaultBoltTaskCompactColumns: Record<string, GridColumn> = {
  taskTemplateName: new GridColumn({
    field: "taskTemplateName",
    headerText: "Task",
    type: "string",
    allowSearching: false,
    allowFiltering: false,
    minWidth: 300,
    maxWidth: 300,
  }),
  taskStatus_AsString: new GridColumn({
    field: "taskStatus_AsString",
    headerText: "Status",
    type: "string",
    allowSearching: false,
    allowFiltering: false,
    minWidth: 300,
    maxWidth: 300,
  }),
  dueDate: new GridColumn({
    field: "dueDate",
    headerText: "Due Date",
    format: { format: "MM/dd/yyyy hh:mm:ss a", type: "date" },
    type: "datetime",
    allowSearching: false,
    allowFiltering: false,
    template: (row: IBoltTask) => {
      return utils.date.formatUtcDateTime((row.dueDate as any) || "", true);
    },
    minWidth: 300,
    maxWidth: 300,
  }),
  id: new GridColumn({
    field: "id",
    headerText: "Identifier",
    type: "string",
    visible: false,
  }),
};
